import { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import {
  DownloadOutlined as DownloadOutlinedIcon,
  FileDownloadOutlined as FileDownloadOutlinedIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import DataExportButton from '../../Components/GatewayContagem/DataExportButton';
import DateRangePopover from '../../Components/GatewayContagem/DateRangePopover';
import GatewayContagemFilters from '../../Components/GatewayContagem/GatewayContagemFilters';
import PageHeader from '../../Components/GatewayContagem/PageHeader';
import useGatewayContagem from '../../Context/Hooks/useGatewayContagem';
import { exportAPI } from '../../API/exportAPI';

const defaultEnd = () => new Date();
const defaultStart = () => {
  const d = new Date();
  d.setDate(d.getDate() - 29);
  d.setUTCHours(0, 0, 0, 0);
  return d;
};

const ExportPage = () => {
  const { t, i18n } = useTranslation();
  const { selectedClient, selectedStore, loadClients, clients } = useGatewayContagem();
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [loadingType, setLoadingType] = useState(null);

  useState(() => { if (!clients.length) loadClients(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartDateChange = useCallback((date) => {
    const startOfDay = new Date(date);
    startOfDay.setUTCHours(0, 0, 0, 0);
    setStartDate(startOfDay);
  }, []);

  const handleEndDateChange = useCallback((date) => {
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);
    setEndDate(endOfDay);
  }, []);

  const EXPORT_TYPES = [
    {
      type: 'dashboard',
      label: t('common:export.dashboard', 'Dashboard (.xlsx)'),
      description: t('common:export.dashboardDesc', 'Visão geral e tráfego diário no período selecionado'),
    },
    {
      type: 'countings',
      label: t('common:export.countings', 'Contagens (.xlsx)'),
      description: t('common:export.countingsDesc', 'Registos de contagem brutos com store name, zone name, entradas e saídas'),
    },
    {
      type: 'dwell',
      label: t('common:export.dwellTime', 'Tempo de Permanência (.xlsx)'),
      description: t('common:export.dwellTimeDesc', 'Número de visitantes, tempos médios, mínimos e máximos de permanência'),
    },
  ];

  const handleDirectExport = async (type) => {
    if (!selectedClient) return;
    setLoadingType(type);
    const clientName = selectedClient?.name || null;
    const storeName = selectedStore?.name || null;
    const lang = i18n.language || 'pt';
    try {
      if (type === 'countings') {
        await exportAPI.exportCountings(selectedClient.id, startDate, endDate, selectedStore?.id, clientName, storeName, lang);
      } else if (type === 'dashboard') {
        await exportAPI.exportDashboard(selectedClient.id, startDate, endDate, selectedStore?.id, clientName, storeName, lang);
      } else if (type === 'dwell') {
        await exportAPI.exportDwellTime(selectedClient.id, startDate, endDate, selectedStore?.id, clientName, storeName, lang);
      }
    } catch (err) {
      console.error('Export failed', err);
    } finally {
      setLoadingType(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('common:export.title', 'Exportar Dados')}
        subtitle={t('common:export.subtitle', 'Exporte dados e relatórios do dashboard para ficheiros Excel')}
      />

      <Card
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '14px',
          boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
        }}
      >
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'text.primary', mb: 2 }}>
            {t('common:common.filter', 'Filtros')}
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
          >
            <GatewayContagemFilters
              showClient={false}
              showStore
              showDateRange={false}
            />
            <DateRangePopover
              startDate={startDate}
              endDate={endDate}
              onStartChange={handleStartDateChange}
              onEndChange={handleEndDateChange}
              startLabel={t('common:from', 'Início')}
              endLabel={t('common:to', 'Fim')}
            />
            <DataExportButton
              clientId={selectedClient?.id}
              startDate={startDate}
              endDate={endDate}
              storeId={selectedStore?.id}
            />
          </Stack>
        </CardContent>
      </Card>

      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '14px',
          overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
        }}
      >
        <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'text.primary' }}>
            {t('common:export.availableExports', 'Exportações Disponíveis')}
          </Typography>
        </Box>

        <List disablePadding>
          {EXPORT_TYPES.map(({ type, label, description }, i) => (
            <ListItem
              key={i}
              divider={i < EXPORT_TYPES.length - 1}
              sx={{
                px: 2.5,
                py: 1.75,
                '&:hover': { bgcolor: '#f9fafb' },
              }}
              secondaryAction={
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={loadingType === type ? <CircularProgress size={15} /> : <DownloadOutlinedIcon sx={{ fontSize: 15 }} />}
                  disabled={!selectedClient || loadingType !== null}
                  onClick={() => handleDirectExport(type)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    borderColor: 'divider',
                    color: 'text.secondary',
                  }}
                >
                  {loadingType === type ? t('common:export.exporting', 'A exportar...') : t('common:export.export', 'Exportar')}
                </Button>
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <FileDownloadOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.primary' }}>
                    {label}
                  </Typography>
                }
                secondary={
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
                    {description}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
};

export default ExportPage;
